import { useEffect, useMemo, useRef, useState } from 'react';
import { signalRService } from '../../services/signalr/SignalRService';
import { HubEvents, SocialEventEnvelope } from '../../services/signalr/types';
import { gameSessionService } from '../../services/api/gameSessionService';
import { FlexoCommander } from './rts/FlexoCommander';
import { DoodleBoard } from './DoodleBoard';
import { GolfHud } from './GolfHud';
import { KitchenHud } from './KitchenHud';
import './GamePanel.css';

/** Authoritative co-op game snapshot (RoomHub.Games.cs BroadcastGameStateAsync). */
interface GameStateBroadcast {
  gameId: string;
  kind: string;
  status: string; // waiting | active | won | lost | reset
  note?: string;
  state: string; // opaque per-kind state document (stringified JSON)
  winners?: string[];
}

/**
 * Per-kind state documents (RoomHub.Games.cs rule modules), unioned into one shape the panel reads
 * defensively (every field optional). Shared: deadline (drives the timer + the generic sync poke).
 *   party-smash: score/combos/target/smashers
 *   hunt/maze/escape (ObjectiveRules/HuntRules): found[]/total/pending/participants
 *   race (RaceRules): next/total/participants
 *   quiz (QuizRules): index/total/q/choices/teamScore/target/scores/answered/lastResult
 */
interface GameState {
  // party-smash
  score?: number;
  combos?: number;
  target?: number;
  smashers?: Record<string, number>;
  // hunt / maze / escape
  found?: string[];
  total?: number;
  requireNear?: number;
  pending?: Record<string, string[]>;
  participants?: string[];
  // race
  next?: number;
  // quiz
  index?: number;
  q?: string;
  choices?: string[];
  teamScore?: number;
  scores?: Record<string, number>;
  answered?: Record<string, number>;
  lastResult?: { index: number; correctIndex: number; correctText?: string; gained: number };
  // connect (question / wyr / hottake / twotruths — turn-based talk games)
  idx?: number;
  phase?: string; // prompt | reveal
  turn?: string; // active userId ("" = none)
  roster?: { id: string; name: string }[];
  names?: Record<string, string>;
  picks?: Record<string, number>;
  submitted?: Record<string, boolean>;
  card?: { Type: string; Text?: string; A?: string; B?: string };
  tt?: { statements?: string[]; lieIndex?: number; guess?: Record<string, number>; revealed?: boolean };
  connections?: number;
  // decode (asymmetric talk-to-win)
  round?: number;
  roleOf?: Record<string, 'guide' | 'operator'>;
  strikes?: number;
  maxStrikes?: number;
  operatorView?: string;
  options?: string[];
  guideManual?: string;
  // golf (minigolf shared scorecard)
  totals?: Record<string, number>;
  onHole?: Record<string, number>;
  holes?: number;
  // kitchen (co-op cooking tickets)
  tickets?: { id: number; dish: string; deadline: string }[];
  missed?: number;
  // shared
  deadline?: string;
}

/**
 * Presentation per GameKind: the collapsed objective (`verb`) plus the present-on-entry intro
 * copy (`title` + one-line `how`). Every kind gets an inviting waiting-state card so a room is
 * never "nothing to do on entry".
 */
const KIND_META: Record<string, { icon: string; verb: string; title: string; how: string }> = {
  'party-smash': { icon: '🏺', verb: 'Smash the pots!', title: '🏺 Pot Smash', how: 'Smash every pot together before the timer runs out.' },
  hunt: { icon: '🔍', verb: 'Hunt down the treasures!', title: '🔍 Treasure Hunt', how: 'Split up and track down every treasure — you both count.' },
  maze: { icon: '🚪', verb: 'Find the way out together!', title: '🚪 Maze Escape', how: 'You BOTH need to reach the exit — find the way out together.' },
  escape: { icon: '🔓', verb: 'Solve the room and escape!', title: '🔓 Escape Room', how: 'You each spot different clues — talk it out and escape together.' },
  race: { icon: '🏁', verb: 'Hit the checkpoints — go!', title: '🏁 Checkpoint Race', how: 'Hit every checkpoint — race side by side to the finish.' },
  quiz: { icon: '❓', verb: 'Quiz time!', title: '❓ Quiz Duo', how: 'Answer together and rack up a shared team score.' },
  connect: { icon: '💬', verb: 'Get to know each other!', title: '💬 Let’s Connect', how: 'Take turns, swap answers, and actually get to know each other.' },
  decode: { icon: '🧩', verb: 'Decode it together!', title: '🧩 Decode Duo', how: 'You each see half the puzzle — talk to each other to win.' },
  rts: { icon: '⚔️', verb: 'Command your army!', title: '⚔️ Flexo Commander', how: 'Build, mass tanks and crush the AI — two commanders, one battle.' },
  doodle: { icon: '🎨', verb: 'Draw and guess!', title: '🎨 Doodle Date', how: 'One of you draws, the other guesses — six rounds, zero art skills required.' },
  golf: { icon: '⛳', verb: 'Tee off!', title: '⛳ Moonlight Greens', how: 'Putt your ball through the course — aim with the camera, hold PUTT to charge. Lowest card wins bragging rights.' },
  kitchen: { icon: '🍳', verb: 'Orders up!', title: '🍳 Kitchen Rush', how: 'Grab, chop, cook, plate, serve — clear the tickets together before closing. Don’t let anything burn!' },
};

/** Rotating banter for the action families — a gentle conversation nudge under the objective. */
const NUDGES: Record<string, string[]> = {
  'party-smash': ['Call out which pot you’re on!', 'Cover each other’s corners.', 'Count them down together — 3, 2, 1!'],
  hunt: ['Ask them: what would you grab first?', 'Shout out where you’re searching.', 'Divide the map — you take left.'],
  maze: ['Describe your path — find each other.', 'Which way did you turn?', 'Meet at the exit together.'],
  escape: ['Say what clue you’re seeing.', 'Compare notes — you see different things.', 'What does yours say?'],
  race: ['Ask which checkpoint they’re at.', 'Pace each other to the finish.', 'Don’t leave your partner behind!'],
};

/** Hot-take agree/disagree scale (choice sent 1..4). */
const HOTTAKE_LABELS = ['Hard no', 'Meh', 'Kinda', 'Totally'];

function fmtClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${r.toString().padStart(2, '0')}` : `${r}s`;
}

/**
 * P5 — the generic co-op game HUD, rendered entirely from the server-authoritative `game_state`
 * broadcast (never client-side game logic). Six families share one frame: a collapsed top-center
 * status strip (objective + progress + timer — the mobile HUD contract, standards §HUD) for the
 * proximity families (party/hunt/maze/escape/race), a dedicated Q&A card for quiz, and a shared
 * win/loss banner. It layers ON TOP of the social floor — chat, circles and invites keep working.
 */
export function GamePanel({ roomId, userId }: { roomId: string; userId?: string }) {
  const [game, setGame] = useState<GameStateBroadcast | null>(null);
  const [banner, setBanner] = useState<{ status: string; note?: string; youWon: boolean } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [quizFlash, setQuizFlash] = useState<GameState['lastResult'] | null>(null);
  // Intro card is dismissable-to-strip, per gameId.
  const [introDismissedFor, setIntroDismissedFor] = useState<string | null>(null);
  // Two-Truths local composer state (active player types 3 statements + marks the lie).
  const [ttStatements, setTtStatements] = useState<string[]>(['', '', '']);
  const [ttLie, setTtLie] = useState<number | null>(null);
  // Rotating conversation-nudge index (action families).
  const [nudgeIdx, setNudgeIdx] = useState(0);

  const bannerTimerRef = useRef<number | null>(null);
  const syncedDeadlineRef = useRef<string | null>(null);
  const onboardedRef = useRef<string | null>(null);
  const lastFlashIdxRef = useRef<number>(-1);

  // ---- Subscribe to authoritative game_state ----
  useEffect(() => {
    const unsub = signalRService.on(HubEvents.SocialEventReceived, (...args: unknown[]) => {
      let env: SocialEventEnvelope;
      try {
        env = JSON.parse(args[0] as string) as SocialEventEnvelope;
      } catch {
        return;
      }
      if (env?.type !== 'game_state' || !env.payloadJson) return;
      let g: GameStateBroadcast;
      try {
        g = JSON.parse(env.payloadJson) as GameStateBroadcast;
      } catch {
        return;
      }
      setGame(g);
      if (g.status === 'won' || g.status === 'lost') {
        const youWon = g.status === 'won' && !!userId && (g.winners ?? []).includes(userId);
        setBanner({ status: g.status, note: g.note, youWon });
        if (bannerTimerRef.current) window.clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = window.setTimeout(() => setBanner(null), 7000);
      } else if (g.status === 'reset') {
        setGame(null);
      }
    });
    // PULL the present-on-entry state: the server's join-time push races this subscription (the
    // panel mounts after the caller-only push already fired), so object-less games (connect/decode/
    // rts) would otherwise never appear. Ask the server to re-push now that we're listening; retry a
    // few times in case the hub connection is still settling right after the room join.
    let tries = 0;
    const pull = () => {
      void signalRService.invoke('RequestGameStates', roomId).catch(() => { /* not ready yet */ });
    };
    pull();
    const pullTimer = window.setInterval(() => {
      tries += 1;
      pull();
      if (tries >= 4) window.clearInterval(pullTimer);
    }, 800);
    return () => {
      unsub();
      window.clearInterval(pullTimer);
      if (bannerTimerRef.current) window.clearTimeout(bannerTimerRef.current);
    };
  }, [userId, roomId]);

  // ---- Timer tick while active ----
  useEffect(() => {
    if (game?.status !== 'active') return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [game?.status]);

  const parsed = useMemo<GameState>(() => {
    if (!game?.state) return {};
    try {
      return JSON.parse(game.state) as GameState;
    } catch {
      return {};
    }
  }, [game?.state]);

  const deadlineMs = parsed.deadline ? Date.parse(parsed.deadline) : 0;
  const remainingMs = deadlineMs ? Math.max(0, deadlineMs - now) : 0;

  // ---- Deadline poke: tick-less kinds — a client whose clock passed the deadline nudges the
  // server's lazy CheckEnd/resolve once (idempotent). Works for every family incl. quiz (per
  // question deadline → one sync per question). ----
  useEffect(() => {
    if (game?.status !== 'active' || !parsed.deadline || !deadlineMs) return;
    if (now < deadlineMs) return;
    if (syncedDeadlineRef.current === parsed.deadline) return;
    syncedDeadlineRef.current = parsed.deadline;
    const gameId = game.gameId;
    const t = window.setTimeout(() => {
      void gameSessionService.sendDelta(roomId, gameId, 'sync');
    }, 800);
    return () => window.clearTimeout(t);
  }, [game?.status, parsed.deadline, deadlineMs, now, roomId, game?.gameId]);

  // ---- Onboarding poke: in an ACTIVE decode game a player who hasn't acted yet has no role
  // (the server builds the roster from deltas). Send one 'sync' so DecodeRules assigns them the
  // operator slot; otherwise the second player is stuck with no view and can't submit. ----
  useEffect(() => {
    if (game?.status !== 'active' || game.kind !== 'decode' || !userId) return;
    if (parsed.roleOf && parsed.roleOf[userId]) return; // already have a role
    if (onboardedRef.current === game.gameId) return;
    onboardedRef.current = game.gameId;
    const gameId = game.gameId;
    const t = window.setTimeout(() => {
      void gameSessionService.sendDelta(roomId, gameId, 'sync');
    }, 400);
    return () => window.clearTimeout(t);
  }, [game?.status, game?.kind, game?.gameId, parsed.roleOf, userId, roomId]);

  // ---- Quiz reveal flash: surface lastResult transiently when a question resolves. ----
  useEffect(() => {
    const lr = parsed.lastResult;
    if (!lr || lr.index === lastFlashIdxRef.current) return;
    lastFlashIdxRef.current = lr.index;
    setQuizFlash(lr);
    const t = window.setTimeout(() => setQuizFlash(null), 2500);
    return () => window.clearTimeout(t);
  }, [parsed.lastResult?.index]);

  // ---- Two-Truths: reset the local composer when the card advances. ----
  useEffect(() => {
    setTtStatements(['', '', '']);
    setTtLie(null);
  }, [parsed.idx]);

  // ---- Rotate the conversation nudge while a game is live. ----
  useEffect(() => {
    if (game?.status !== 'active') return;
    const id = window.setInterval(() => setNudgeIdx((n) => n + 1), 6000);
    return () => window.clearInterval(id);
  }, [game?.status]);

  if (!game && !banner) return null;

  // RTS (Flexo Commander) takes over the whole panel with its own canvas + netcode. It syncs via
  // the SocialEvent rts_* relay, NOT the game_delta path — so its "Start battle" sends rts_start,
  // never a game_delta 'start' (which would hit unknown_kind). The waiting game_state that mounts
  // this arrives from SendInitialGameStatesToCallerAsync (present-on-entry), no rule module needed.
  if (game && game.kind === 'rts') {
    return <FlexoCommander roomId={roomId} gameId={game.gameId} userId={userId} />;
  }

  // Doodle Date takes over the panel with its shared canvas. The generic hooks above
  // (game_state subscription, deadline sync poke) keep serving it.
  if (game && game.kind === 'doodle') {
    return <DoodleBoard roomId={roomId} game={game} userId={userId} />;
  }

  const kind = game?.kind ?? '';
  const meta = KIND_META[kind] || { icon: '🎮', verb: 'Play!', title: '🎮 Game', how: 'Play together!' };
  const isQuiz = kind === 'quiz';
  const isRace = kind === 'race';
  const isObjective = kind === 'hunt' || kind === 'maze' || kind === 'escape';
  const isParty = kind === 'party-smash';
  const isConnect = kind === 'connect';
  const isDecode = kind === 'decode';
  const isGolf = kind === 'golf';
  const isKitchen = kind === 'kitchen';
  const isAction = isParty || isObjective || isRace; // proximity families → the strip HUD
  const active = game?.status === 'active';
  const waiting = game?.status === 'waiting';
  // Present-on-entry: a prominent intro card for every kind, dismissable to the strip.
  const showIntro = waiting && !!game && introDismissedFor !== game.gameId;
  const nudge = active && isAction ? (NUDGES[kind]?.[nudgeIdx % (NUDGES[kind]?.length || 1)] ?? '') : '';
  const sendDelta = (action: string, payload?: Record<string, unknown>) => {
    if (!game) return;
    void gameSessionService.sendDelta(roomId, game.gameId, action, payload);
  };
  const nameOf = (uid?: string): string =>
    (uid && (parsed.names?.[uid] || parsed.roster?.find((r) => r.id === uid)?.name)) || 'Partner';

  // Shared current/total progress (per family).
  const current = isRace
    ? Math.max(0, (parsed.next ?? 1) - 1)
    : isObjective
      ? parsed.found?.length ?? 0
      : isParty
        ? parsed.score ?? 0
        : 0;
  const total = isParty ? parsed.target ?? 0 : parsed.total ?? 0;
  const progress = total > 0 ? Math.min(1, current / total) : 0;
  const players = parsed.participants?.length ?? (parsed.smashers ? Object.keys(parsed.smashers).length : 0);
  const yourSmashes = userId && parsed.smashers ? parsed.smashers[userId] ?? 0 : 0;
  const awaitingPartner = parsed.pending ? Object.keys(parsed.pending).length : 0;

  const objective = (() => {
    if (isObjective && total > 0) {
      if (kind === 'maze') return `Reach the exit — ${current}/${total} there`;
      if (kind === 'escape') return `Solve the room — ${current}/${total} done`;
      return `Find all ${total} — ${current} found`;
    }
    if (isRace && total > 0) return `Checkpoint ${current}/${total}`;
    return meta.verb;
  })();

  // ---- Quiz Q&A card ----
  const myAnswer = userId && parsed.answered ? parsed.answered[userId] : undefined;
  const iAnswered = myAnswer !== undefined;
  const answerCount = parsed.answered ? Object.keys(parsed.answered).length : 0;
  const sendAnswer = (choiceIndex: number) => {
    if (!game || iAnswered) return;
    void gameSessionService.sendDelta(roomId, game.gameId, 'answer', {
      questionIndex: parsed.index ?? 0,
      choiceIndex,
    });
  };

  // ---- Connect (turn-based talk games) derived ----
  const card = parsed.card;
  const cardType = card?.Type ?? '';
  const isMyTurn = !!userId && parsed.turn === userId;
  const isReveal = parsed.phase === 'reveal';
  const iPicked = !!userId && parsed.picks ? parsed.picks[userId] !== undefined : false;
  const activeName = nameOf(parsed.turn);
  const ttStatementsLive = parsed.tt?.statements ?? [];
  const activeSubmitted = ttStatementsLive.length === 3;
  const myGuess = userId && parsed.tt?.guess ? parsed.tt.guess[userId] : undefined;
  const iGuessed = myGuess !== undefined;
  const ttReadyToSubmit = ttStatements.every((s) => s.trim().length > 0) && ttLie !== null;

  // ---- Decode (asymmetric) derived ----
  const myRole = userId ? parsed.roleOf?.[userId] : undefined;

  return (
    <>
      {/* Present-on-entry: a prominent, inviting intro card for every kind. */}
      {game && showIntro && (
        <div className="game-intro" role="group" aria-label="Game invitation">
          <div className="game-intro-card">
            <span className="game-intro-icon" aria-hidden="true">{meta.icon}</span>
            <h3 className="game-intro-title">{meta.title}</h3>
            <p className="game-intro-how">{meta.how}</p>
            <button type="button" className="game-intro-start" onClick={() => sendDelta('start')}>
              ▶ Start together
            </button>
            <button
              type="button"
              className="game-intro-dismiss"
              onClick={() => setIntroDismissedFor(game.gameId)}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {game && active && isGolf && (
        <GolfHud
          state={{ totals: parsed.totals, onHole: parsed.onHole, holes: parsed.holes, roster: parsed.roster }}
          userId={userId}
        />
      )}

      {game && active && isKitchen && (
        <KitchenHud
          roomId={roomId}
          gameId={game.gameId}
          state={{ tickets: parsed.tickets, score: parsed.score, target: parsed.target, missed: parsed.missed }}
        />
      )}

      {game && active && isConnect && (
        <div className="game-connect" role="group" aria-label="Connect" aria-live="polite">
          <div className="game-connect-head">
            <span className="game-connect-count">
              {meta.icon} {(parsed.idx ?? 0) + 1}/{parsed.total ?? 0}
            </span>
            {parsed.connections !== undefined && (
              <span className="game-connect-conn">✦ {parsed.connections} shared</span>
            )}
          </div>

          {/* question — read aloud & discuss, either taps Next */}
          {cardType === 'question' && (
            <>
              <p className="game-connect-turn">
                {isMyTurn ? 'Your turn to read aloud 🎤' : `${activeName}’s turn to read`}
              </p>
              <p className="game-connect-prompt">{card?.Text}</p>
              <button type="button" className="game-connect-next" onClick={() => sendDelta('next')}>
                Next ▸
              </button>
            </>
          )}

          {/* would-you-rather — both pick privately, then reveal both */}
          {cardType === 'wyr' && (
            <>
              <p className="game-connect-prompt">{card?.Text}</p>
              {!isReveal ? (
                iPicked ? (
                  <p className="game-connect-wait">Waiting for your partner…</p>
                ) : (
                  <div className="game-connect-ab">
                    <button type="button" className="game-connect-choice" onClick={() => sendDelta('pick', { choice: 0 })}>
                      {card?.A ?? 'A'}
                    </button>
                    <button type="button" className="game-connect-choice" onClick={() => sendDelta('pick', { choice: 1 })}>
                      {card?.B ?? 'B'}
                    </button>
                  </div>
                )
              ) : (
                <>
                  <ul className="game-connect-reveal">
                    {(parsed.roster ?? Object.keys(parsed.picks ?? {}).map((id) => ({ id, name: nameOf(id) }))).map((r) => {
                      const c = parsed.picks?.[r.id];
                      return (
                        <li key={r.id}>
                          <strong>{r.id === userId ? 'You' : nameOf(r.id)}:</strong>{' '}
                          {c === 0 ? card?.A : c === 1 ? card?.B : '—'}
                        </li>
                      );
                    })}
                  </ul>
                  <button type="button" className="game-connect-next" onClick={() => sendDelta('next')}>
                    Next ▸
                  </button>
                </>
              )}
            </>
          )}

          {/* hot take — agree/disagree scale, then reveal both */}
          {cardType === 'hottake' && (
            <>
              <p className="game-connect-prompt">{card?.Text}</p>
              {!isReveal ? (
                iPicked ? (
                  <p className="game-connect-wait">Waiting for your partner…</p>
                ) : (
                  <div className="game-connect-scale">
                    {HOTTAKE_LABELS.map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        className="game-connect-scale-btn"
                        onClick={() => sendDelta('pick', { choice: i + 1 })}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )
              ) : (
                <>
                  <ul className="game-connect-reveal">
                    {(parsed.roster ?? Object.keys(parsed.picks ?? {}).map((id) => ({ id, name: nameOf(id) }))).map((r) => {
                      const v = parsed.picks?.[r.id];
                      return (
                        <li key={r.id}>
                          <strong>{r.id === userId ? 'You' : nameOf(r.id)}:</strong>{' '}
                          {v ? HOTTAKE_LABELS[v - 1] ?? v : '—'}
                        </li>
                      );
                    })}
                  </ul>
                  <button type="button" className="game-connect-next" onClick={() => sendDelta('next')}>
                    Next ▸
                  </button>
                </>
              )}
            </>
          )}

          {/* two truths and a lie */}
          {cardType === 'twotruths' && (
            <>
              {parsed.tt?.revealed ? (
                <div className="game-connect-tt-reveal">
                  <p className="game-connect-prompt">The lie was…</p>
                  <ul className="game-connect-reveal">
                    {ttStatementsLive.map((s, i) => (
                      <li key={i} className={i === parsed.tt?.lieIndex ? 'lie' : ''}>
                        {i === parsed.tt?.lieIndex ? '🤥 ' : '✅ '}{s}
                      </li>
                    ))}
                  </ul>
                  {!isMyTurn && myGuess !== undefined && (
                    <p className={`game-connect-verdict ${myGuess === parsed.tt?.lieIndex ? 'good' : 'bad'}`}>
                      {myGuess === parsed.tt?.lieIndex ? '🎉 You spotted the lie!' : 'Not quite — got you!'}
                    </p>
                  )}
                  <button type="button" className="game-connect-next" onClick={() => sendDelta('next')}>
                    Next ▸
                  </button>
                </div>
              ) : isMyTurn ? (
                activeSubmitted ? (
                  <p className="game-connect-wait">Waiting for {nameOf(parsed.roster?.find((r) => r.id !== userId)?.id)} to guess…</p>
                ) : (
                  <div className="game-connect-tt-form">
                    <p className="game-connect-turn">Write 3 things — 2 true, 1 lie. Mark the lie.</p>
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="game-connect-tt-row">
                        <input
                          type="text"
                          className="game-connect-tt-input"
                          placeholder={`Statement ${i + 1}`}
                          value={ttStatements[i]}
                          onChange={(e) =>
                            setTtStatements((prev) => prev.map((s, j) => (j === i ? e.target.value : s)))
                          }
                        />
                        <button
                          type="button"
                          className={`game-connect-tt-lie ${ttLie === i ? 'on' : ''}`}
                          onClick={() => setTtLie(i)}
                          title="Mark as the lie"
                        >
                          {ttLie === i ? '🤥 Lie' : 'Lie?'}
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="game-connect-next"
                      disabled={!ttReadyToSubmit}
                      onClick={() => sendDelta('submitLie', { statements: ttStatements, lieIndex: ttLie })}
                    >
                      Submit ▸
                    </button>
                  </div>
                )
              ) : activeSubmitted ? (
                iGuessed ? (
                  <p className="game-connect-wait">Guess locked — waiting for the reveal…</p>
                ) : (
                  <>
                    <p className="game-connect-turn">Which one is {activeName}’s lie?</p>
                    <div className="game-connect-ab vertical">
                      {ttStatementsLive.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className="game-connect-choice"
                          onClick={() => sendDelta('guess', { index: i })}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                )
              ) : (
                <p className="game-connect-wait">Waiting for {activeName} to write their three…</p>
              )}
            </>
          )}
        </div>
      )}

      {game && active && isDecode && (
        <div className="game-decode" role="group" aria-label="Decode" aria-live="polite">
          <div className="game-decode-head">
            <span className="game-decode-count">
              {meta.icon} Round {(parsed.round ?? 0) + 1}/{parsed.total ?? 0}
            </span>
            {parsed.deadline && <span className="game-decode-timer">⏱ {fmtClock(remainingMs)}</span>}
            <span className="game-decode-strikes">❌ {parsed.strikes ?? 0}/{parsed.maxStrikes ?? 3}</span>
          </div>

          {myRole === 'guide' ? (
            <>
              <p className="game-decode-role">You are the GUIDE 📖</p>
              <div className="game-decode-manual">{parsed.guideManual}</div>
              <p className="game-decode-hint">Tell your partner what to do — only they can act.</p>
            </>
          ) : myRole === 'operator' ? (
            <>
              <p className="game-decode-role">You are the OPERATOR 🛠️</p>
              <div className="game-decode-view">{parsed.operatorView}</div>
              <div className="game-decode-options">
                {(parsed.options ?? []).map((o, i) => (
                  <button
                    key={i}
                    type="button"
                    className="game-decode-option"
                    onClick={() => sendDelta('submit', { choice: i })}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="game-decode-role">Assigning roles…</p>
          )}
          <p className="game-decode-talk">🗣 Talk to each other!</p>
        </div>
      )}

      {game && active && isQuiz && (
        <div className="game-quiz" role="group" aria-label="Quiz" aria-live="polite">
          <div className="game-quiz-head">
            <span className="game-quiz-count">
              {meta.icon} Q{(parsed.index ?? 0) + 1}/{parsed.total ?? 0}
            </span>
            <span className="game-quiz-score">⭐ {parsed.teamScore ?? 0}/{parsed.target ?? 0}</span>
            {parsed.deadline && <span className="game-quiz-timer">⏱ {fmtClock(remainingMs)}</span>}
          </div>
          {quizFlash && (
            <p className={`game-quiz-flash ${quizFlash.gained > 0 ? 'good' : 'bad'}`}>
              {quizFlash.gained > 0
                ? `✓ Correct! +${quizFlash.gained}`
                : `✗ The answer: ${quizFlash.correctText ?? ''}`}
            </p>
          )}
          <p className="game-quiz-q">{parsed.q}</p>
          <div className="game-quiz-choices">
            {(parsed.choices ?? []).map((c, i) => (
              <button
                key={i}
                type="button"
                className={`game-quiz-choice ${iAnswered && myAnswer === i ? 'picked' : ''}`}
                disabled={iAnswered}
                onClick={() => sendAnswer(i)}
              >
                {c}
              </button>
            ))}
          </div>
          <p className="game-quiz-foot">
            {iAnswered ? 'Answer locked — waiting…' : 'Tap your answer'}
            {answerCount > 0 && ` · ${answerCount} in`}
          </p>
        </div>
      )}

      {game && ((active && isAction) || (waiting && !showIntro)) && (
        <div className={`game-panel ${expanded ? 'expanded' : ''}`} role="status" aria-live="polite">
          <button
            type="button"
            className="game-panel-strip"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            title="Game status — tap for details"
          >
            <span className="game-panel-icon" aria-hidden="true">{meta.icon}</span>
            {active ? (
              <>
                <span className="game-panel-score">
                  {current}
                  {total > 0 && <span className="game-panel-target">/{total}</span>}
                </span>
                {parsed.deadline && <span className="game-panel-timer">⏱ {fmtClock(remainingMs)}</span>}
              </>
            ) : (
              <span className="game-panel-objective">{meta.verb}</span>
            )}
            <span className="game-panel-caret" aria-hidden="true">{expanded ? '▲' : '▼'}</span>
          </button>

          {active && total > 0 && (
            <div className="game-panel-bar" aria-hidden="true">
              <div className="game-panel-bar-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          )}

          {nudge && <p className="game-panel-nudge">💬 {nudge}</p>}

          {expanded && (
            <div className="game-panel-details">
              <p className="game-panel-verb">{objective}</p>
              <div className="game-panel-stats">
                {active && isParty && (
                  <>
                    <span>⚡ {parsed.combos ?? 0} combos</span>
                    <span>👥 {players} smashing</span>
                    {userId && <span>You: {yourSmashes}</span>}
                  </>
                )}
                {active && (isObjective || isRace) && (
                  <>
                    <span>👥 {players} playing</span>
                    {awaitingPartner > 0 && <span>🤝 {awaitingPartner} awaiting partner</span>}
                  </>
                )}
              </div>
              {waiting && (
                <button
                  type="button"
                  className="game-panel-start"
                  onClick={() => void gameSessionService.sendDelta(roomId, game.gameId, 'start')}
                >
                  ▶ Start
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {banner && (
        <div className={`game-banner ${banner.status} ${banner.status === 'won' ? 'celebrate' : ''}`} role="alert">
          <div className="game-banner-card">
            {banner.status === 'won' && (
              <div className="game-banner-confetti" aria-hidden="true">
                <span>🎉</span><span>✨</span><span>💖</span><span>🎊</span><span>⭐</span>
              </div>
            )}
            <span className="game-banner-emoji" aria-hidden="true">
              {banner.status === 'won' ? '🎉' : '💫'}
            </span>
            <p className="game-banner-title">
              {banner.status === 'won' ? 'You did it — together!' : 'So close!'}
            </p>
            {banner.note && <p className="game-banner-note">{banner.note}</p>}
            {banner.youWon && <p className="game-banner-reward">✦ Sparks earned!</p>}
            {game && (
              <button
                type="button"
                className="game-banner-again"
                onClick={() => {
                  setBanner(null);
                  void gameSessionService.sendDelta(roomId, game.gameId, 'reset');
                }}
              >
                {banner.status === 'won' ? '↻ Play again' : '↻ Try again'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default GamePanel;
